import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormControlLabel, Switch, useTheme } from '@mui/material';
import { useEffect } from 'react';

import { UnitForm, schema } from '../../../../../validations/unit.validation';

import { Section, Unit, UnitType } from '../../../../../entities';
import { Input, Autocomplete, ScrollableTabPanel } from '../../../../../components';

interface UnitsPanelProps {
  visible: boolean;
  unit: Unit;
  sections: Section[];
  onClickClose: () => void;
  onSubmit: (value: Unit) => void;
}

const UnitTypeOptionsLabel: Record<UnitType, string> = {
  [UnitType.Apartment]: 'Apartamento',
  [UnitType.OfficeSpace]: 'Escritório',
};

export const UnitsPanel = ({ visible, unit, sections, onClickClose, onSubmit }: UnitsPanelProps): JSX.Element => {
  const {
    register,
    reset,
    handleSubmit: handleValidation,
    setValue,
    getValues,
    control,
    formState: { errors, isSubmitting },
  } = useForm<UnitForm>({
    resolver: yupResolver(schema),
    mode: 'onChange',
  });

  const sectionId = useWatch({ control, name: 'sectionId' });
  const type = useWatch({ control, name: 'type' });
  const isDisabled = useWatch({ control, name: 'disabled' });

  useEffect(() => {
    reset({
      label: unit.label || '',
      number: unit.number || '',
      sectionId: unit.sectionId,
      size: unit.size,
      type: unit.type as UnitType,
      disabled: unit.disabled,
    });
  }, [unit, reset]);

  const { palette } = useTheme();

  const handleSubmit = handleValidation(() => {
    const { size, ...values } = getValues();

    return onSubmit({ ...unit, ...values, size: size || undefined });
  });
  return (
    <ScrollableTabPanel
      direction='left'
      title="Descrição da unidade"
      visible={visible}
      loading={isSubmitting}
      onClickClose={onClickClose}
      onClickSubmit={handleSubmit}
    >
      <Input
        label="nome da unidade"
        placeholder="Ex: Mezanino"
        validationRegister={register('label')}
        error={Boolean(errors.label)}
        helperText={errors.label?.message || ' '}
      />
      <Input
        label="número"
        placeholder="Ex: M101"
        validationRegister={register('number')}
        error={Boolean(errors.number)}
        helperText={errors.number?.message || ' '}
      />
      <Input
        label="tamanho"
        placeholder="Ex: 172m"
        validationRegister={register('size')}
        error={Boolean(errors.size)}
        helperText={errors.size?.message || ' '}
      />
      <Autocomplete
        options={Object.keys(UnitTypeOptionsLabel)}
        value={type || null}
        getOptionLabel={(option) => UnitTypeOptionsLabel[option as UnitType]}
        label="tipo"
        placeholder="Ex: Apartamento"
        noOptionsText="Nada aqui..."
        helperText={errors.type?.message || ' '}
        error={Boolean(errors.type)}
        onChange={(_, value) => setValue('type', value as UnitType)}
      />
      <Autocomplete
        options={sections}
        value={sections.find((s) => s.id === sectionId) || null}
        getOptionLabel={(option) => option.label}
        onChange={(_, value) => setValue('sectionId', value?.id)}
        label="bloco"
        helperText={' '}
        placeholder="Ex: 1"
        noOptionsText="Nada aqui..."
      />
      <FormControlLabel
        label="Está habilitada"
        style={{ marginLeft: '1px', marginRight: '0' }}
        sx={{ color: palette.grey[100] }}
        control={
          <Switch checked={!isDisabled} value={!isDisabled} onChange={() => setValue('disabled', !isDisabled)} />
        }
      />
    </ScrollableTabPanel>
  );
};

export default UnitsPanel;
